import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGenerateProgram } from '../hooks/usePrograms';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import type { GenerateProgramRequest } from '../api/danceApi';

export function GenerateProgramPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const generateProgram = useGenerateProgram();

  const [formData, setFormData] = useState<GenerateProgramRequest>({
    prompt: '',
    experienceLevel: 'mixed',
    danceCount: 8,
    energyArc: 'building'
  });

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
        <p>Please <Link to="/login">log in</Link> to generate a program.</p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const program = await generateProgram.mutateAsync(formData);
      navigate(`/programs/${program.programId}`);
    } catch {
      // error displayed below
    }
  };

  const inputStyle = {
    width: '100%',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    fontSize: '1rem',
    border: '1.5px solid #e8e4f3',
    borderRadius: 'var(--radius-sm)',
    transition: 'border-color var(--transition-base)',
    fontFamily: 'inherit'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 'var(--spacing-sm)',
    fontWeight: '700',
    color: 'var(--color-text)'
  };

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <form onSubmit={handleSubmit} className="card" style={{ padding: 'var(--spacing-xl)' }}>
        <h2 style={{ marginTop: 0, marginBottom: 'var(--spacing-sm)', fontWeight: 800 }}>
          Generate a Program with AI
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-lg)' }}>
          Describe your event and AI will curate an ordered program from your dance library.
        </p>

        <div style={{
          padding: 'var(--spacing-md)',
          marginBottom: 'var(--spacing-xl)',
          backgroundColor: 'var(--color-primary-light)',
          borderLeft: '4px solid var(--color-primary)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.9rem',
          color: 'var(--color-primary)'
        }}>
          AI selects and orders dances from your existing library. Add more dances first to give it more to work with.
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="prompt" style={labelStyle}>Describe your event *</label>
          <textarea
            id="prompt"
            required
            value={formData.prompt}
            onChange={e => setFormData({ ...formData, prompt: e.target.value })}
            rows={4}
            placeholder="e.g., A friendly community dance at a local barn, mix of regulars and newcomers, warm and social atmosphere. We love swings and want people to end the night smiling."
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-xl)' }}>
          <div>
            <label htmlFor="experienceLevel" style={labelStyle}>Experience level</label>
            <select
              id="experienceLevel"
              value={formData.experienceLevel}
              onChange={e => setFormData({ ...formData, experienceLevel: e.target.value as GenerateProgramRequest['experienceLevel'] })}
              style={inputStyle}
            >
              <option value="beginner">Beginner-friendly</option>
              <option value="mixed">Mixed crowd</option>
              <option value="experienced">Experienced</option>
            </select>
          </div>

          <div>
            <label htmlFor="danceCount" style={labelStyle}>Number of dances</label>
            <select
              id="danceCount"
              value={formData.danceCount}
              onChange={e => setFormData({ ...formData, danceCount: Number(e.target.value) })}
              style={inputStyle}
            >
              <option value={5}>5 dances</option>
              <option value={8}>8 dances</option>
              <option value={10}>10 dances</option>
              <option value={12}>12 dances</option>
            </select>
          </div>

          <div>
            <label htmlFor="energyArc" style={labelStyle}>Energy arc</label>
            <select
              id="energyArc"
              value={formData.energyArc}
              onChange={e => setFormData({ ...formData, energyArc: e.target.value as GenerateProgramRequest['energyArc'] })}
              style={inputStyle}
            >
              <option value="gentle">Gentle throughout</option>
              <option value="building">Builds to a peak</option>
              <option value="high-energy">High energy all night</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={generateProgram.isPending}
            className="btn btn-primary"
          >
            {generateProgram.isPending ? 'Generating...' : 'Generate Program'}
          </button>
          <button type="button" onClick={() => navigate('/programs')} className="btn btn-secondary">
            Cancel
          </button>
        </div>

        {generateProgram.isPending && (
          <div style={{
            marginTop: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: 'var(--color-primary-light)',
            color: 'var(--color-primary)',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid var(--color-primary)'
          }}>
            Building your program... This may take 10–20 seconds.
          </div>
        )}

        {generateProgram.isError && (
          <div style={{
            marginTop: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid var(--color-danger)'
          }}>
            <strong>Error:</strong> {generateProgram.error.message}
          </div>
        )}
      </form>
    </div>
  );
}
